/**
 * Ecommerce-research SSE pipeline → store subscriber.
 * Migrated in Phase 8 commit 76.
 *
 * Owns the `ecommerceSubPhases` state machine: each store-connect
 * branch decides which sub-phase list to seed (Shopify vs manual file
 * upload) and downstream phase events flip individual sub-phase
 * statuses via `updateSubPhase`.
 */

import { sseEvents } from "./sseEvents";
import { toThoughtEvent } from "../store/department/thoughtStream";
import type {
  CampaignData,
  CoAgentMessage,
  StepStatus,
  ThoughtEvent,
} from "../store/department/types";
import {
  STORE_CONNECT_IDS,
  PRODUCT_METRICS_NEEDED,
} from "../store/department/pipelines/ecommerceHighlights";

interface EcommerceSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  addThoughtEvent: (stepIndex: number, event: Omit<ThoughtEvent, "id" | "ts">) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
  clearWidgetHighlight: (widgetId: string) => void;
  highlightWidget: (
    widgetId: string,
    comment: string,
    durationMs?: number,
    actionLabel?: string,
    stepIndex?: number,
    onAction?: () => void,
  ) => void;
  getCampaignData: () => CampaignData;
  startEcommerceMetrics: () => void;
}

let wired = false;

export function wireEcommerceSubscriber(
  getHooks: () => EcommerceSubscriberHooks,
): void {
  if (wired) return;
  wired = true;

  const thought = (
    step: number,
    phase: string,
    status: string,
    data?: Record<string, unknown>,
    progress?: Record<string, unknown>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ev = toThoughtEvent("ecommerce", phase, status, data as any, progress as any);
    if (ev) getHooks().addThoughtEvent(step, ev);
  };

  const updateSubPhase = (
    phaseKey: string,
    newStatus: "pending" | "running" | "complete" | "error",
  ) => {
    const hooks = getHooks();
    const current = hooks.getCampaignData().ecommerceSubPhases;
    if (!current) return;
    hooks.updateCampaignData({
      ecommerceSubPhases: current.map((sp) =>
        sp.phase === phaseKey ? { ...sp, status: newStatus } : sp,
      ),
    });
  };

  sseEvents.subscribe("ecommerce:session-named", ({ name }) => {
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("ecommerce:file-extraction-detecting-schema", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "file_extraction",
      ecommerceFileExtractionStatus: "detecting_schema",
    });
    updateSubPhase("file_schema_detection", "running");
    thought(1, "file_schema_detection", "running");
    hooks.addStepLog(1, "Reading your file...");
  });

  sseEvents.subscribe("ecommerce:file-extraction-extracting-products", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({ ecommerceFileExtractionStatus: "extracting_products" });
    updateSubPhase("file_schema_detection", "complete");
    updateSubPhase("file_product_extraction", "running");
    thought(1, "file_schema_detection", "complete");
    thought(1, "file_product_extraction", "running");
    hooks.addStepLog(1, "Pulling products from your file...");
  });

  sseEvents.subscribe("ecommerce:file-extraction-preview", ({ data }) => {
    const hooks = getHooks();
    const products = (data.extraction as { products?: unknown[] } | undefined)?.products;
    const productCount = Array.isArray(products) ? products.length : 0;
    hooks.updateCampaignData({
      ecommerceFileExtractionStatus: "preview",
      ecommerceExtractionResult:
        (data.extraction ?? null) as CampaignData["ecommerceExtractionResult"],
    });
    updateSubPhase("file_product_extraction", "complete");
    thought(1, "file_product_extraction", "complete", { count: productCount });
    hooks.addStepLog(1, `[SUCCESS] Found ${productCount} products — ready for review`);
  });

  sseEvents.subscribe("ecommerce:file-extraction-complete", ({ productCount }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ ecommerceFileExtractionStatus: "complete" });
    hooks.addStepLog(1, `[SUCCESS] ${productCount} products confirmed`);
  });

  sseEvents.subscribe("ecommerce:file-extraction-error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ ecommerceFileExtractionStatus: "error" });
    updateSubPhase("file_schema_detection", "error");
    updateSubPhase("file_product_extraction", "error");
    thought(1, "error", "error", { message });
    hooks.addStepLog(1, `[ERROR] ${message}`);
  });

  sseEvents.subscribe("ecommerce:store-connect-running", ({ shopDomain, data }) => {
    const hooks = getHooks();
    for (const id of STORE_CONNECT_IDS) hooks.clearWidgetHighlight(id);
    hooks.updateCampaignData({
      ecommerceResearchPhase: "store_connect",
      ecommerceResearchProgress: { phase: "store_connect", completed: 0, total: 8 },
    });
    thought(0, "shopify_connect", "running", data);
    hooks.addStepLog(0, `Connecting to ${shopDomain || "store"}...`);
  });

  sseEvents.subscribe("ecommerce:store-connect-complete", ({ data }) => {
    const hooks = getHooks();
    const isManualConnection = data.connectionMethod === "manual";
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "store_connect", completed: 1, total: 8 },
      ecommerceStoreId: data.storeId as CampaignData["ecommerceStoreId"],
      shopInfo: data as CampaignData["shopInfo"],
      ecommerceSubPhases: isManualConnection
        ? [
            { phase: "file_schema_detection", label: "Reading your file", status: "pending" },
            { phase: "file_product_extraction", label: "Pulling products", status: "pending" },
            { phase: "brand_analysis", label: "Analyzing brand", status: "pending" },
            { phase: "product_categorization", label: "Categorizing products", status: "pending" },
          ]
        : [
            { phase: "product_import", label: "Importing products", status: "pending" },
            { phase: "brand_analysis", label: "Analyzing brand", status: "pending" },
            { phase: "product_categorization", label: "Categorizing products", status: "pending" },
          ],
    });
    if (isManualConnection) {
      thought(0, "manual_store_created", "complete", data);
    } else {
      thought(0, "shopify_connect", "complete", data);
    }
    const shopName = data.shopName as string | undefined;
    const shopDomain = data.shopDomain as string | undefined;
    hooks.addStepLog(
      0,
      isManualConnection
        ? `[SUCCESS] Manual store created for ${shopName}`
        : `[SUCCESS] Connected to ${shopName ?? shopDomain}`,
    );
    hooks.setStepState((state) => ({
      currentStepIndex: 1,
      stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
    }));
    hooks.setFocusedStep(1);
  });

  sseEvents.subscribe("ecommerce:store-connect-error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchStatus: "error",
      ecommerceResearchPhase: "error",
    });
    thought(0, "error", "error", { message });
    hooks.addStepLog(0, `[ERROR] ${message}`);
  });

  sseEvents.subscribe("ecommerce:product-import-running", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "product_import",
      ecommerceResearchProgress: { phase: "product_import", completed: 1, total: 8 },
    });
    updateSubPhase("product_import", "running");
    thought(1, "product_import", "running", data, { completed: 0, total: data.total });
    hooks.addStepLog(1, "Importing products...");
  });

  sseEvents.subscribe("ecommerce:product-import-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "product_import", completed: 2, total: 8 },
      ecommerceProductCount: Number(data.count ?? 0),
      ecommerceProducts: (data.products ?? []) as CampaignData["ecommerceProducts"],
      ecommerceCatalogSummary: data.catalogSummary as CampaignData["ecommerceCatalogSummary"],
    });
    updateSubPhase("product_import", "complete");
    thought(1, "product_import", "complete", data);
    if (data.catalogSummary) {
      const catalogSummary = data.catalogSummary as {
        activeProducts?: unknown;
        priceRange?: { min?: unknown; max?: unknown };
      };
      thought(1, "catalog_summary", "complete", {
        activeProducts: catalogSummary.activeProducts,
        priceMin: catalogSummary.priceRange?.min,
        priceMax: catalogSummary.priceRange?.max,
      });
    }
    hooks.addStepLog(1, `[SUCCESS] Imported ${data.count ?? 0} products`);
  });

  sseEvents.subscribe("ecommerce:brand-analysis-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "brand_analysis",
      ecommerceResearchProgress: { phase: "brand_analysis", completed: 2, total: 8 },
    });
    updateSubPhase("brand_analysis", "running");
    thought(1, "brand_analysis", "running");
    hooks.addStepLog(1, "Analyzing brand positioning...");
  });

  sseEvents.subscribe("ecommerce:brand-analysis-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "brand_analysis", completed: 3, total: 8 },
      ecommerceBrandProfile: data as CampaignData["ecommerceBrandProfile"],
    });
    updateSubPhase("brand_analysis", "complete");
    thought(1, "brand_analysis", "complete", data);
    const positioning = data.pricePositioning as string | undefined;
    hooks.addStepLog(1, `[SUCCESS] Brand profile: ${positioning ?? "mid-range"} positioning`);
  });

  sseEvents.subscribe("ecommerce:product-categorization-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchPhase: "product_categorization",
      ecommerceResearchProgress: { phase: "product_categorization", completed: 3, total: 8 },
    });
    updateSubPhase("product_categorization", "running");
    thought(1, "categorization", "running");
    hooks.addStepLog(1, "Categorizing products for ad targeting...");
  });

  sseEvents.subscribe("ecommerce:product-categorization-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchProgress: { phase: "product_categorization", completed: 4, total: 8 },
      ecommerceCategories: (data.categories ?? []) as CampaignData["ecommerceCategories"],
    });
    updateSubPhase("product_categorization", "complete");
    thought(1, "categorization", "complete", data);
    hooks.addStepLog(1, `[SUCCESS] Categorized ${data.count ?? 0} products`);
    hooks.startEcommerceMetrics();
    // WAIT GATE: stay on Step 1 — user must confirm before advancing to Estimates
    hooks.setStepState((state) => ({
      currentStepIndex: state.currentStepIndex,
      stepStatuses: { ...state.stepStatuses, 1: "waiting" },
    }));
    hooks.highlightWidget(
      PRODUCT_METRICS_NEEDED.widgetId,
      PRODUCT_METRICS_NEEDED.comment,
      PRODUCT_METRICS_NEEDED.durationMs,
      PRODUCT_METRICS_NEEDED.actionLabel,
      PRODUCT_METRICS_NEEDED.stepIndex,
      () => {
        if (PRODUCT_METRICS_NEEDED.stepIndex !== undefined) {
          hooks.setFocusedStep(PRODUCT_METRICS_NEEDED.stepIndex);
        }
      },
    );
  });

  sseEvents.subscribe("ecommerce:done", ({ data }) => {
    const hooks = getHooks();
    thought(1, "research_done", "complete");
    hooks.updateCampaignData({
      ecommerceStoreId: data.storeId as CampaignData["ecommerceStoreId"],
      ecommerceBrandProfile: data.brandProfile as CampaignData["ecommerceBrandProfile"],
      ecommerceProductCount: data.productCount as CampaignData["ecommerceProductCount"],
      ecommerceCategories: data.categories as CampaignData["ecommerceCategories"],
      shopInfo: data.shopInfo as CampaignData["shopInfo"],
      totalCreditsUsed: data.totalCreditsUsed as CampaignData["totalCreditsUsed"],
      ecommerceSubPhases: undefined,
    });
  });

  sseEvents.subscribe("ecommerce:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      ecommerceResearchStatus: "error",
      ecommerceResearchPhase: "error",
      ecommerceLastErrorMessage: message,
    });
    thought(0, "error", "error", { message });
    hooks.addStepLog(0, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting" },
    }));
  });
}
